/* The "Send feedback" dialog (APP-104 §3.3). Tokens only; the look follows ConfirmDialog. */
.overlay {
  position: fixed;
  inset: 0;
  z-index: 70;
  display: grid;
  place-items: center;
  padding: 16px;
  background: var(--scrim);
}

.overlay[data-entering] {
  animation: fade var(--dur-fast) ease-out;
}

@keyframes fade {
  from {
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .overlay[data-entering] {
    animation: none;
  }
}

.modal {
  width: min(640px, 100%);
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  border-radius: var(--radius-md);
  background: var(--white);
  box-shadow: var(--shadow-dialog);
}

.modal::before {
  content: '';
  display: block;
  height: 4px;
  background: var(--gradient-rule);
}

.dialog {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 24px 28px 28px;
  outline: none;
}

.title {
  margin: 0;
  color: var(--text-heading);
  font-size: 24px;
  line-height: 1.15;
  letter-spacing: 0.02em;
}

.form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.tip {
  margin: 8px 0 0;
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.5;
}

.tip kbd {
  padding: 2px 6px;
  border: 1px solid var(--gray-250);
  border-radius: var(--radius-sm);
  background: var(--gray-25);
  color: var(--text-heading);
  font: 600 13px/1.4 var(--font-ui);
}

.legend,
.sectionLabel {
  margin: 0 0 8px;
  color: var(--text-heading);
  font-size: 14px;
  font-weight: var(--fw-semibold);
}

.chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: var(--control-height);
  padding: 0 16px;
  border: 1px solid var(--border-control);
  border-radius: var(--radius-pill);
  background: var(--white);
  color: var(--brand-navy);
  font-size: 14px;
  font-weight: var(--fw-medium);
  cursor: pointer;
}

/* The chosen chip is navy and carries a tick, so the choice never rests on colour alone. */
.chip[data-selected] {
  border-color: var(--brand-navy);
  background: var(--brand-navy);
  color: var(--white);
  font-weight: var(--fw-semibold);
}

.chip[data-focus-visible] {
  outline: none;
  box-shadow: var(--focus-halo);
}

.shot {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

/* D29: the preview and the checkbox's row are one size from the moment the dialog opens, whether
   the picture is being taken, is ready or couldn't be taken, so it arriving never moves Send. A
   shorter window gets a smaller preview rather than a taller dialog. */
.preview {
  position: relative;
  display: grid;
  place-items: center;
  height: clamp(120px, 30vh, 280px);
  overflow: hidden;
  border: 1px solid var(--border-card);
  border-radius: var(--radius-sm);
  background: var(--surface-soft);
  color: var(--text-secondary);
  font-size: 14px;
}

/* Out of the flow: the picture's own size can't change the box. */
.preview img {
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.include {
  display: flex;
  align-items: center;
  min-height: var(--control-height);
}

.previewNote {
  padding: 16px;
  text-align: center;
}

.check {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: var(--control-height);
  color: var(--text-heading);
  font-size: 15px;
  cursor: pointer;
}

.box {
  display: grid;
  flex: none;
  place-items: center;
  width: 22px;
  height: 22px;
  border: 2px solid var(--brand-navy);
  border-radius: 4px;
  background: var(--white);
  color: var(--white);
}

.check[data-selected] .box {
  background: var(--brand-navy);
}

.check[data-focus-visible] .box {
  box-shadow: var(--focus-halo);
}

.alsoIncluded {
  display: flex;
  flex-direction: column;
}

.alsoIncluded .sectionLabel {
  margin: 0;
}

.chevron {
  margin-left: 6px;
  transition: transform var(--dur-fast) ease;
}

.included[open] .chevron {
  transform: rotate(180deg);
}

@media (prefers-reduced-motion: reduce) {
  .chevron {
    transition: none;
  }
}

.included summary {
  display: inline-flex;
  align-items: center;
  min-height: var(--control-height);
  color: var(--brand-petrol);
  font-size: 14px;
  font-weight: var(--fw-semibold);
  cursor: pointer;
}

.included summary:focus-visible {
  outline: none;
  box-shadow: var(--focus-halo);
}

.included ul {
  margin: 0 0 8px;
  padding-left: 20px;
  color: var(--text-secondary);
  font-size: 14px;
  line-height: 1.6;
}

.goesTo {
  margin: 0;
  color: var(--text-secondary);
  font-size: 14px;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
}

.problem {
  margin: 0;
  padding: 10px 14px;
  border-left: 3px solid var(--status-problem-ink);
  border-radius: var(--radius-sm);
  background: var(--status-problem-bg);
  color: var(--status-problem-ink);
  font-size: 14px;
  line-height: 1.5;
}

.confirm {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 24px 28px 28px;
  outline: none;
}
